import { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import { CloseIcon, SearchIcon } from './Icons';
import { formatPrice } from '../utils/formatPrice';
import { getCategoryLabel, searchMenuItems } from '../utils/searchMenu';
import { useCart } from '../context/CartContext';
import { createCartItemFromProduct, isComposableProduct } from '../utils/cartItem';

const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 6;

function HeaderSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();
  const listboxId = useId();
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [barHovered, setBarHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const results = query.trim().length >= MIN_QUERY_LENGTH
    ? searchMenuItems(query, { limit: MAX_RESULTS })
    : [];

  useEffect(() => {
    const mobileMq = window.matchMedia('(max-width: 900px)');
    const apply = () => setIsMobile(mobileMq.matches);
    apply();
    mobileMq.addEventListener('change', apply);
    return () => mobileMq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (location.pathname === '/menu' && q) {
      setQuery(q);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!expanded) return undefined;

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setExpanded(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded]);

  useEffect(() => {
    if (expanded) {
      const timer = setTimeout(() => inputRef.current?.focus(), 180);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [expanded]);

  const closeSearch = () => {
    setExpanded(false);
    setBarHovered(false);
    setActiveIndex(-1);
  };

  const goToMenuSearch = (value) => {
    const trimmed = value.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return;
    navigate(`/menu?q=${encodeURIComponent(trimmed)}`);
    closeSearch();
    inputRef.current?.blur();
  };

  const selectProduct = (item) => {
    if (isComposableProduct(item)) {
      navigate(`/menu?open=${encodeURIComponent(item.id)}`);
    } else {
      addItem(createCartItemFromProduct(item));
    }
    setQuery(item.name);
    closeSearch();
    inputRef.current?.blur();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (activeIndex >= 0 && results[activeIndex]) {
      selectProduct(results[activeIndex]);
      return;
    }
    goToMenuSearch(query);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeSearch();
      return;
    }

    if (results.length === 0) {
      if (event.key === 'Enter') {
        event.preventDefault();
        goToMenuSearch(query);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? results.length - 1 : current - 1));
    }
  };

  const showResults = expanded && query.trim().length >= MIN_QUERY_LENGTH;

  const showCloseLead = barHovered || isMobile;

  return (
    <div className={`header-search ${expanded ? 'is-open' : ''}`} ref={rootRef}>
      <div
        className="header-search-track"
        id="header-search-panel"
        aria-hidden={!expanded}
      >
        <form
          className="header-search-form"
          role="search"
          onSubmit={handleSubmit}
          onMouseEnter={() => setBarHovered(true)}
          onMouseLeave={() => setBarHovered(false)}
        >
          <label htmlFor="header-search-input" className="sr-only">
            Rechercher un plat ou une boisson
          </label>
          <button
            type="button"
            className={`header-search-lead ${showCloseLead ? 'is-close' : ''}`}
            onClick={() => {
              if (showCloseLead) {
                closeSearch();
              } else {
                inputRef.current?.focus();
              }
            }}
            aria-label={showCloseLead ? 'Fermer la recherche' : 'Rechercher'}
            tabIndex={expanded ? 0 : -1}
          >
            {showCloseLead ? <CloseIcon size={16} /> : <SearchIcon size={16} />}
          </button>
          <input
            id="header-search-input"
            ref={inputRef}
            type="search"
            className="header-search-input"
            placeholder="Rechercher un plat, un jus…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            tabIndex={expanded ? 0 : -1}
            role="combobox"
            aria-expanded={showResults}
            aria-controls={listboxId}
            aria-autocomplete="list"
          />
          {query && expanded && (
            <button
              type="button"
              className="header-search-clear"
              onClick={() => {
                setQuery('');
                setActiveIndex(-1);
                inputRef.current?.focus();
              }}
              aria-label="Effacer la recherche"
            >
              <CloseIcon size={14} />
            </button>
          )}
        </form>
      </div>

      {!expanded && (
        <button
          type="button"
          className="search-toggle"
          onClick={() => setExpanded(true)}
          aria-label="Ouvrir la recherche"
          aria-expanded={false}
          aria-controls="header-search-panel"
        >
          <SearchIcon size={20} />
        </button>
      )}

      {showResults && (
        <div className="header-search-dropdown" id={listboxId} role="listbox">
          {results.length > 0 ? (
            <>
              <ul className="header-search-results">
                {results.map((item, index) => (
                  <li key={item.id} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      className={`header-search-result ${index === activeIndex ? 'is-active' : ''}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => selectProduct(item)}
                    >
                      <span className="header-search-result-image">
                        <OptimizedImage image={item.image} alt="" loading="lazy" />
                      </span>
                      <span className="header-search-result-body">
                        <span className="header-search-result-name">{item.name}</span>
                        <span className="header-search-result-meta">
                          {getCategoryLabel(item.category)} · {formatPrice(item.basePrice)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="header-search-view-all"
                onClick={() => goToMenuSearch(query)}
              >
                Voir tous les résultats pour « {query.trim()} »
              </button>
            </>
          ) : (
            <p className="header-search-empty">Aucun résultat pour « {query.trim()} »</p>
          )}
        </div>
      )}
    </div>
  );
}

export default HeaderSearch;
